import type { HotelContractModel } from "../../models"

export type DayWiseSurchargeDisplayRow = {
    rowKey: string
    label: string
    amount: number | null
    isPercentage?: boolean
    day_wise_pricing_group_surcharge: {
        flag_different_surcharge: boolean
        surcharge_amount: number | null
        flag_surcharge_amount_is_percentage?: boolean
    }[];
}

export type SeasonalExtraBedDisplayRow = {
    seasonId: string
    seasonName: string
    charges: number | null
    isPercentage?: boolean
}

export const shouldHideAdultExtraBedInSurcharge = (contract: HotelContractModel | null | undefined): boolean =>
    Boolean(contract?.flag_seasonal_extra_bed_pricing)

/** Day-wise groups with surcharge flag — same source as Rates tab `dayWiseSurchargeRows`. */
export const getDayWiseSurchargeDisplayRows = (
    contract: HotelContractModel | null | undefined,
): DayWiseSurchargeDisplayRow[] => {
    if (!contract?.flag_day_wise_pricing) return []
    const groups = contract.day_wise_pricing || []
    return [...groups]
        .filter((g) => g.flag_surchange_applied)
        .sort(
            (a, b) =>
                (typeof a.sequence === "number" && Number.isFinite(a.sequence) ? a.sequence : 0) -
                    (typeof b.sequence === "number" && Number.isFinite(b.sequence) ? b.sequence : 0) ||
                String(a.day_wise_pricing_group_id ?? "").localeCompare(String(b.day_wise_pricing_group_id ?? "")),
        )
        .map((g) => ({
            ...g,
            rowKey: String(g.day_wise_pricing_group_id ?? ""),
            label: (g.day_wise_pricing_group_name || "").trim() || "Day-wise group",
            amount: g.surcharge_amount ?? null,
            isPercentage: g.flag_surcharge_amount_is_percentage === true,
            day_wise_pricing_group_surcharge: (g.day_wise_pricing_group_surcharge || []).map((s) => ({
                flag_different_surcharge: s.flag_different_surcharge,
                surcharge_amount: s.surcharge_amount,
                flag_surcharge_amount_is_percentage: s.flag_surcharge_amount_is_percentage === true,
            })),
        }))
}

/** Per-season extra bed charges aligned to contract seasons (Rates tab parity). */
export const getSeasonalExtraBedDisplayRows = (
    contract: HotelContractModel | null | undefined,
): SeasonalExtraBedDisplayRow[] => {
    if (!contract?.flag_seasonal_extra_bed_pricing) return []
    const fromPricing: Record<string, { charges: number | null; isPercentage: boolean }> = {}
    ;(contract.seasonal_extra_bed_pricing || []).forEach((p) => {
        if (p.season_id) {
            fromPricing[p.season_id] = {
                charges: p.charges ?? null,
                isPercentage: p.flag_charges_is_percentage === true,
            }
        }
    })
    return (contract.season || [])
        .filter((s) => s.season_id && s.season_name)
        .map((s) => ({
            seasonId: s.season_id!,
            seasonName: s.season_name!,
            charges: fromPricing[s.season_id!]?.charges ?? null,
            isPercentage: fromPricing[s.season_id!]?.isPercentage === true,
        }))
}
